import { readableNumber } from "./text"

const TOOLTIP_POST_COUNT = 3

const THEME_LABELS = {
  solutions: "Solution Requests",
  pain: "Pain & Anger",
  resources: "Advice Requests",
  ideas: "Ideas",
  money: "Money Talk",
  opportunities: "Opportunities",
  self: "Self-Promotion",
  news: "News",
}

const unslugify = slug =>
  slug
    .replace(/-/g, " ")
    .replace(
      /\w\S*/g,
      text => text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
    )

const topPostTitlesByScore = (posts, limit) =>
  [...posts]
    .sort((a, b) => (b.score || 0) - (a.score || 0))
    .map(p => p.title)
    .filter(Boolean)
    .slice(0, limit)

const getThemes = (posts, analysis, limit = 3) => {
  if (!analysis || !posts?.results) return []
  const postsById = Object.fromEntries(posts.results.map(p => [p.id, p]))
  const themesMap = {}

  Object.entries(analysis).forEach(([postId, entry]) => {
    const slug = entry.classify?.raw
    if (!slug || !THEME_LABELS[slug]) return
    const post = postsById[postId]
    if (!post) return
    if (!themesMap[slug]) {
      themesMap[slug] = { slug, label: THEME_LABELS[slug], count: 0, posts: [] }
    }
    themesMap[slug].count += 1
    themesMap[slug].posts.push(post)
  })

  return Object.values(themesMap)
    .sort((a, b) => b.count - a.count)
    .slice(0, limit)
    .map(t => ({
      slug: t.slug,
      label: t.label,
      count: t.count,
      snippets: topPostTitlesByScore(t.posts, TOOLTIP_POST_COUNT),
    }))
}

const getTopics = (posts, analysis, limit = 3) => {
  if (!analysis || !posts?.results) return []
  const topicsMap = Object.create(null)

  Object.entries(analysis).forEach(([postId, entry]) => {
    if (postId === "all") return
    const raw = entry?.topics
    let slugs = []
    if (Array.isArray(raw)) {
      slugs = raw.filter(t => typeof t === "string" && t)
    } else if (typeof raw === "string" && raw) {
      slugs = [raw]
    } else if (raw && typeof raw === "object") {
      slugs = Object.keys(raw)
    }
    slugs.forEach(t => {
      if (!Array.isArray(topicsMap[t])) topicsMap[t] = []
      topicsMap[t].push(postId)
    })
  })

  return Object.keys(topicsMap)
    .map(t => {
      const postIds = topicsMap[t]
      const label = unslugify(t)
      const matchingPosts = posts.results.filter(p => postIds.includes(p.id))
      const keywordPosts = matchingPosts.filter(p =>
        p.title?.toLowerCase().includes(label.toLowerCase())
      )
      const postsForSnippets =
        keywordPosts.length > 0 ? keywordPosts : matchingPosts
      return {
        label,
        count: postIds.length,
        snippets: topPostTitlesByScore(postsForSnippets, TOOLTIP_POST_COUNT),
      }
    })
    .sort((a, b) => b.count - a.count)
    .slice(0, limit)
}

const getProductsFromAnalysis = (analysis, posts, limit = 3) => {
  if (!analysis) return []
  const postsById = posts?.results
    ? Object.fromEntries(posts.results.map(p => [p.id, p]))
    : {}
  const productsMap = {}
  Object.entries(analysis).forEach(([postId, entry]) => {
    ;(entry.products || []).forEach(p => {
      const label = typeof p === "string" ? p : p?.name || p?.label
      if (!label) return
      if (!productsMap[label]) {
        productsMap[label] = { count: 0, posts: [] }
      }
      productsMap[label].count += 1
      const post = postsById[postId]
      if (post) productsMap[label].posts.push(post)
    })
  })
  return Object.keys(productsMap)
    .map(label => ({
      label,
      count: productsMap[label].count,
      snippets: topPostTitlesByScore(
        productsMap[label].posts,
        TOOLTIP_POST_COUNT
      ),
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit)
}

const getFlair = (posts, limit = 3) => {
  const flairMap = {}
  const postsByFlair = {}
  ;(posts?.results || []).forEach(p => {
    if (!p.flair_label) return
    flairMap[p.flair_label] = (flairMap[p.flair_label] || 0) + 1
    if (!postsByFlair[p.flair_label]) postsByFlair[p.flair_label] = []
    postsByFlair[p.flair_label].push(p)
  })
  return Object.keys(flairMap)
    .map(label => ({
      label,
      count: flairMap[label],
      snippets: topPostTitlesByScore(postsByFlair[label], TOOLTIP_POST_COUNT),
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit)
}

const getProducts = (gummysearch, posts, limit = 3) => {
  const fromField = (gummysearch.products || [])
    .filter(p => p?.name)
    .map(p => ({
      label: p.name,
      count: p.count || 0,
      snippets: (p.titles || []).slice(0, TOOLTIP_POST_COUNT),
    }))
  if (fromField.length) {
    return fromField.sort((a, b) => b.count - a.count).slice(0, limit)
  }
  return getProductsFromAnalysis(gummysearch.results_analysis, posts, limit)
}

const joinList = items => {
  if (!items.length) return ""
  if (items.length === 1) return items[0]
  if (items.length === 2) return `${items[0]} and ${items[1]}`
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`
}

const buildGummysearchGlanceParagraph = gummysearch => {
  if (!gummysearch) return null

  const calculated = gummysearch.calculated || {}
  const posts = gummysearch.results_submissions_top
  const yearlyMembers = calculated.per_year_members

  const topics = getTopics(posts, gummysearch.results_analysis, 3).map(
    t => t.label
  )
  const themes = getThemes(posts, gummysearch.results_analysis, 2).map(
    t => t.label
  )
  const products = getProducts(gummysearch, posts, 3).map(p => p.label)

  const sentences = []

  if (yearlyMembers != null && yearlyMembers > 0) {
    sentences.push(
      `Roughly ${readableNumber(
        yearlyMembers
      )} members have joined in the past year`
    )
  }
  if (topics.length) {
    sentences.push(`Popular discussion topics include ${joinList(topics)}`)
  }
  if (themes.length) {
    sentences.push(`Common discussion themes are ${joinList(themes)}`)
  }
  if (products.length) {
    sentences.push(
      `Product recommendations often mention ${joinList(products)}`
    )
  }

  if (!sentences.length) return null

  return `${sentences.join(". ")}.`
}

export {
  getThemes,
  getTopics,
  getFlair,
  getProducts,
  buildGummysearchGlanceParagraph,
}
